/* eslint-disable arrow-body-style */
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { addDays } from 'date-fns';
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  setDoc,
  doc,
  deleteDoc,
  getDoc,
  collectionGroup,
  Timestamp,
  updateDoc,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import {
  EventDate,
  EventDateRange,
  EventType,
  DeepLinkResponse,
  EventDjs,
  EventStats,
} from 'src/@types/event';
import { Organisation, Ticket } from 'src/@types/events_org';
import { Reservation, ReservationTicket, Client, Request } from 'src/@types/events_reservations';
import { NotificationType } from 'src/@types/notification_forms';
import { OrderType } from 'src/@types/order_forms';
import { ProductCategoryType } from 'src/@types/productCategory_forms';
import { ProductType, EventProductCategories } from 'src/@types/product_forms';
import { Store, StoreType } from 'src/@types/store_forms';
import { RoomType, TableType } from 'src/@types/table_forms';

import { DB, FUNCTIONS } from 'src/contexts/FirebaseContext';
import { httpsCallable } from 'firebase/functions';
import { Point, SeatMapData, Table } from 'src/@types/seatMapper';
import { MailCampaign, BaseCampaignFields, CompleteCampaignFields } from 'src/@types/mailcampaign';
import { String } from 'lodash';

interface UpdatePromotersRequest {
  id?: string;
  eventid: string;
  referralid: string;
  displayName: string;
  email: string;
  type?: string;
}

interface UpdateEventCoverRequest {
  id: string;
  coverImage: string;
}

interface UpdateEventInvoiceInfosRequest {
  id: string;
  isInvoiceRequred: boolean;
  invoiceInfo: InvoiceField[];
}

interface InvoiceField {
  field: string;
  required: boolean;
}

interface UpdateEventAccountIdRequest {
  id: string;
  accountId: string;
}

interface UpdateEventLogoRequest {
  id: string;
  logoImage: string;
}
interface UpdateEventCoverResponse {
  coverImage: string;
}

interface UpdateEventLogoResponse {
  logoImage: string;
}
interface UpdateStoreCoverRequest {
  id: string;
  image: string;
}

interface UpdateStoreCoverResponse {
  image: string;
}
interface UpdateInvitationRequest {
  senderID: string;
  invitationData: { userID: string; message: string; email: string; status: string };
}

interface deleteInvitationRequest {
  userID: string;
  senderID: string;
}

interface TicketBasicResponse {
  name: string;
  description: string;
  initialQuantity: number;
  Quantity?: number;
  is_sold_out?: boolean;
  hide_sale_dates?: boolean;
  order: number;
  price: number;
  maxQuantityPerOrder: number;
  minQuantityPerOrder: number;
  SellTimeLimit: EventDateRange;
  accessTimeThreshold: Date | Timestamp | null;
  promoCode?: {
    code: string;
    dicount: number;
  };
  eventId: string;
}

export interface UserRequest {
  uid: string;
  email?: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  about?: string;
  photoURL?: string;
  isNewUser?: boolean;
  accountId?: string;
  ownerIds?: [];
}

export interface StoreRequest {
  id: string;
  ownerid?: string;
  name?: string;
  description?: string;
  address?: string;
  phone?: string;
  prepTime?: string;
  image?: string;
  open?: boolean;
  mode?: string;
  isrushourclient?: boolean;
  qrcodeid?: string;
  zeltyapikey?: string;
}

export interface ProductRequest {
  id: string;
  categoryid?: string;
  description?: string;
  hs?: boolean;
  image?: string;
  isalcoholic?: boolean;
  iscomposite?: boolean;
  name?: string;
  price?: number;
  showid?: string;
  storeid?: string;
  tier?: string;
  eventid?: string;
}

export interface OrderRequest {
  id: string;
  total?: number;
  orderNumber?: string;
  status?: string;
  showid?: string;
  storeid?: string;
  client?: string;
  products?: Array<any>;
  appversion?: string;
  paymentIntent?: string;
  clientid?: string;
  origin?: string;
  checkoutSession?: string;
  pickMode?: string;
  tier?: string;
}

export interface NotificationRequest {
  id: string;
  title?: string;
  description?: string;
  avatar?: string | null;
  type?: string;
  createdAt?: Date;
  isUnRead?: boolean;
}

export interface TicketBasicRequest {
  id: string;
  startDate?: Date;
  endDate?: Date;
}

export interface EventsBasicRequest {
  userid: string;
  orgid: string;
  role: string;
}

const basicTicket: TicketBasicResponse = {
  name: 'Ticket basique',
  description: 'Entry to the concert',
  initialQuantity: 0,
  order: 0,
  price: 0,
  maxQuantityPerOrder: 4,
  minQuantityPerOrder: 1,
  SellTimeLimit: {
    start: new Date(),
    end: addDays(new Date(), 1) as Date,
  },
  accessTimeThreshold: new Date(),
  eventId: '',
};

export interface OrgsReq {
  orgOwnId: string;
  isAuthorized: boolean;
  isOwner: boolean;
}

// Define a service using a base URL and expected endpoints
export const EventsApi = createApi({
  reducerPath: 'EventsApi',
  baseQuery: fakeBaseQuery(),
  refetchOnReconnect: true,
  tagTypes: [
    'Event',
    'Events',
    'Organisation',
    'Ticket',
    'User',
    'Store',
    'Room',
    'Product',
    'Category',
    'Order',
    'Notification',
    'Reservation',
    'SeatMap',
    'Client',
    'MailCampaign',
    'EventStats',
  ],
  endpoints: (builder) => ({
    //events
    fetchEventsWithOrg: builder.query<EventType[], string>({
      async queryFn(authId) {
        const constraints = [];
        constraints.push(where('ownerid', '==', authId));
        let eventsRef = collection(DB, 'events');
        let orgsRef = collection(DB, 'organisers');
        const qOrg = query(orgsRef, ...constraints);
        const qEvents = query(eventsRef, ...constraints);
        let eventsData: EventType[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : undefined,
          startDate: (doc.data()?.startDate as Timestamp).toDate(),
          endDate: (doc.data()?.endDate as Timestamp).toDate(),

          datesReaccuring: doc.data()?.datesReaccuring
            ? ([
                ...(doc.data()?.datesReaccuring?.map((date: any) => ({
                  start: (date.start as Timestamp).toDate(),
                  end: (date.end as Timestamp).toDate(),
                })) || []),
              ] as EventDateRange[])
            : undefined,
        }));
        let orgData: Organisation[] = (await getDocs(qOrg)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        let eventsWithOrgData: EventType[] = eventsData.map((event: EventType) => {
          return { ...event, org: orgData.find((org: any) => org.id === event.organisation) };
        });
        console.log('eventsWithOrgData', eventsWithOrgData);
        return { data: eventsWithOrgData };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    fetchEventWithTickets: builder.query<EventType, string>({
      async queryFn(eventId) {
        let eventsDocRef = doc(DB, 'events', eventId);
        let eventData = (await getDoc(eventsDocRef)).data() as EventType;
        eventData.id = eventId;
        eventData.createdAt = eventData?.createdAt
          ? ((eventData?.createdAt as Timestamp).toDate() as EventDate)
          : undefined;
        eventData.startDate = eventData?.startDate
          ? ((eventData?.startDate as Timestamp).toDate() as EventDate)
          : addDays(Date.now(), 0);
        eventData.endDate = eventData?.endDate
          ? ((eventData?.endDate as Timestamp).toDate() as EventDate)
          : addDays(Date.now(), 1);
        eventData.datesReaccuring = eventData?.datesReaccuring
          ? ([
              ...(eventData?.datesReaccuring?.map((date: any) => ({
                start: date.start ? (date.start as Timestamp).toDate() : undefined,
                end: date.end ? (date.end as Timestamp).toDate() : undefined,
              })) || []),
            ] as EventDateRange[])
          : undefined;

        let ticketsRef = collection(DB, 'events', eventId, 'tickets');
        const qTickets = query(ticketsRef, where('eventId', '==', eventId));
        let ticketsData: Ticket[] = (await getDocs(qTickets)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          //SellTimeLimit has start and end as Timestamps so we need to convert them to Date
          SellTimeLimit: {
            start: doc.data().SellTimeLimit?.start
              ? (doc.data().SellTimeLimit?.start as Timestamp).toDate()
              : undefined,
            end: doc.data().SellTimeLimit?.end
              ? (doc.data().SellTimeLimit?.end as Timestamp).toDate()
              : undefined,
          },
          accessTimeThreshold: {
            start: doc.data().accessTimeThreshold?.start
              ? (doc.data().accessTimeThreshold?.start as Timestamp).toDate()
              : undefined,
            end: doc.data().accessTimeThreshold?.end
              ? (doc.data().accessTimeThreshold?.end as Timestamp).toDate()
              : undefined,
          },
        }));
        console.log({ ...eventData, tickets: ticketsData });
        return { data: { ...eventData, tickets: ticketsData } };
      },
      providesTags: (result, error, eventId) => [{ type: 'Event', id: eventId }],
    }),
    fetchEvent: builder.query<EventType, string>({
      async queryFn(eventId) {
        let eventsDocRef = doc(DB, 'events', eventId);
        let eventData = (await getDoc(eventsDocRef)).data() as EventType;
        eventData.id = eventId;
        eventData.createdAt = eventData?.createdAt
          ? ((eventData?.createdAt as Timestamp).toDate() as EventDate)
          : addDays(Date.now(), 0);
        eventData.startDate = eventData?.startDate
          ? ((eventData?.startDate as Timestamp).toDate() as EventDate)
          : addDays(Date.now(), 0);
        eventData.endDate = eventData?.endDate
          ? ((eventData?.endDate as Timestamp).toDate() as EventDate)
          : addDays(Date.now(), 1);
        eventData.stopSellDate = eventData?.stopSellDate
          ? ((eventData?.stopSellDate as Timestamp).toDate() as EventDate)
          : undefined;
        eventData.datesReaccuring = eventData?.datesReaccuring
          ? ([
              ...(eventData?.datesReaccuring?.map((date: any) => ({
                start: date.start ? (date.start as Timestamp).toDate() : undefined,
                end: date.end ? (date.end as Timestamp).toDate() : undefined,
              })) || []),
            ] as EventDateRange[])
          : undefined;
        return { data: eventData };
      },
      providesTags: (result, error, eventId) => [
        { type: 'Event', id: eventId },
        { type: 'Events', id: eventId },
      ],
    }),
    fetchEvents: builder.query<EventType[], EventsBasicRequest>({
      async queryFn(eventsRequest) {
        const constraints = [];
        console.log(eventsRequest);
        if (eventsRequest.role === 'Owner') {
          constraints.push(where('ownerid', '==', eventsRequest.userid));
        } else {
          constraints.push(where('organisation', '==', eventsRequest.orgid));
        }

        let eventsRef = collection(DB, 'events');
        const qEvents = query(eventsRef, ...constraints);
        let eventsData: EventType[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0),
          startDate: doc.data()?.startDate
            ? (doc.data()?.startDate as Timestamp).toDate()
            : addDays(Date.now(), 0),
          endDate: doc.data()?.endDate
            ? (doc.data()?.endDate as Timestamp).toDate()
            : addDays(Date.now(), 1),
          datesReaccuring: doc.data()?.datesReaccuring
            ? ([
                ...(doc.data()?.datesReaccuring?.map((date: any) => ({
                  start: date.start ? (date.start as Timestamp).toDate() : undefined,
                  end: date.end ? (date.end as Timestamp).toDate() : undefined,
                })) || []),
              ] as EventDateRange[])
            : undefined,
        }));
        //console.log('eventsData', eventsData);
        return { data: eventsData };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result && result?.length > 0
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id: id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    fetchEventsByOrg: builder.query<EventType[], string>({
      async queryFn(orgId) {
        const eventsRef = collection(DB, 'events');

        const mapEventDoc = (doc: any): EventType => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0),
          startDate: doc.data()?.startDate
            ? (doc.data()?.startDate as Timestamp).toDate()
            : addDays(Date.now(), 0),
          endDate: doc.data()?.endDate
            ? (doc.data()?.endDate as Timestamp).toDate()
            : addDays(Date.now(), 1),
          datesReaccuring: doc.data()?.datesReaccuring
            ? ([
                ...(doc.data()?.datesReaccuring?.map((date: any) => ({
                  start: date.start ? (date.start as Timestamp).toDate() : undefined,
                  end: date.end ? (date.end as Timestamp).toDate() : undefined,
                })) || []),
              ] as EventDateRange[])
            : undefined,
        });

        // Query 1: Events where org is the main organizer
        const qOwned = query(eventsRef, where('organisation', '==', orgId));
        const ownedEvents: EventType[] = (await getDocs(qOwned)).docs.map(mapEventDoc);

        // Query 2: Events where org is a co-organizer
        const qCoOrg = query(eventsRef, where('coOrganizerIds', 'array-contains', orgId));
        const coOrgEvents: EventType[] = (await getDocs(qCoOrg)).docs.map((d) => ({
          ...mapEventDoc(d),
          _isCoOrganized: true,
        }));

        // Merge and deduplicate
        const seenIds = new Set(ownedEvents.map((e) => e.id));
        const uniqueCoOrgEvents = coOrgEvents.filter((e) => !seenIds.has(e.id));
        const allEvents = [...ownedEvents, ...uniqueCoOrgEvents];

        return { data: allEvents };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result && result?.length > 0
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id: id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    fetchAllEvents: builder.query<EventType[], string>({
      async queryFn(authId) {
        const constraints = [];
        constraints.push(where('creationStatus', '==', 'published'));
        let eventsRef = collection(DB, 'events');
        const qEvents = query(eventsRef, ...constraints);
        let eventsData: EventType[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0),
          startDate: doc.data()?.startDate
            ? (doc.data()?.startDate as Timestamp).toDate()
            : addDays(Date.now(), 0),
          endDate: doc.data()?.endDate
            ? (doc.data()?.endDate as Timestamp).toDate()
            : addDays(Date.now(), 1),
          datesReaccuring: doc.data()?.datesReaccuring
            ? ([
                ...(doc.data()?.datesReaccuring?.map((date: any) => ({
                  start: date.start ? (date.start as Timestamp).toDate() : undefined,
                  end: date.end ? (date.end as Timestamp).toDate() : undefined,
                })) || []),
              ] as EventDateRange[])
            : undefined,
        }));
        //console.log('eventsData', eventsData);
        return { data: eventsData };
      },
      //provide tags typescript evnt and event id to be used in invalidatesTags
      // map the result to an array of tags  [{type: 'Event', id: event.id}]
      providesTags: (result, error, authId) =>
        result && result?.length > 0
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id: id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    fetchEventWithOrg: builder.query<EventType, string>({
      async queryFn(eventId) {
        let eventsDocRef = doc(DB, 'events', eventId);
        let orgsRef = collection(DB, 'organisers');
        const qOrg = query(orgsRef, where('id', '==', eventId));
        let eventData = (await getDoc(eventsDocRef)).data() as EventType;
        eventData.startDate = (eventData?.startDate as Timestamp).toDate();
        eventData.endDate = (eventData?.endDate as Timestamp).toDate();

        eventData.datesReaccuring = eventData?.datesReaccuring
          ? [
              ...(eventData?.datesReaccuring?.map((date) => ({
                start: (date.start as Timestamp).toDate(),
                end: (date.end as Timestamp).toDate(),
              })) || []),
            ]
          : undefined;

        let orgData: Organisation[] = (await getDocs(qOrg)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        let eventWithOrgData: EventType = {
          ...eventData,
          org: orgData.find((org: any) => org.id === eventData?.organisation),
        };
        console.log('eventWithOrgData', eventWithOrgData);
        return { data: eventWithOrgData };
      },
      providesTags: (result, error, eventId) => [{ type: 'Event', id: eventId }],
    }),
    fetchEventsWithOrgAndTickets: builder.query<EventType[], string>({
      async queryFn(authId) {
        // get collection of events and subcollerction of tickets for each event
        const constraints = [];
        constraints.push(where('ownerid', '==', authId));
        let eventsRef = collection(DB, 'events');
        let orgsRef = collection(DB, 'organisers');
        const qOrg = query(orgsRef, ...constraints);
        const qEvents = query(eventsRef, ...constraints);
        let eventsData: EventType[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        let orgData: Organisation[] = (await getDocs(qOrg)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        let eventsWithOrgData: EventType[] = eventsData.map((event) => {
          return {
            ...event,
            org: orgData.find((org: Organisation) => org.id === event.organisation),
          };
        });
        let eventsWithTickets: EventType[] = await Promise.all(
          eventsWithOrgData.map(async (event: EventType) => {
            let ticketsRef = collection(DB, 'events', event.id, 'tickets');
            const qTickets = query(ticketsRef, where('event', '==', event.id));
            let ticketsData: Ticket[] = (await getDocs(qTickets)).docs.map((doc: any) => ({
              id: doc.id,
              ...doc.data(),
            }));
            return { ...event, tickets: ticketsData };
          })
        );
        return { data: eventsWithTickets };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    //orgs
    fetchOrgs: builder.query<Organisation[], OrgsReq>({
      async queryFn(orgReq) {
        let orgData: Organisation[] = [];
        const { orgOwnId, isAuthorized, isOwner } = orgReq;
        if (!isAuthorized) {
          return { data: [] };
        }
        const constraints = [];
        if (isOwner) {
          console.log('isOwner', isOwner);
          console.log('orgOwnId', orgOwnId);
          constraints.push(where('ownerid', '==', orgOwnId));
          let orgsRef = collection(DB, 'organisers');
          const qOrg = query(orgsRef, ...constraints);
          orgData = (await getDocs(qOrg)).docs.map((doc: any) => ({
            id: doc.id,
            ...doc.data(),
          }));
          return { data: orgData };
        } else {
          let orgsDocRef = doc(DB, 'organisers', orgOwnId);
          const orgData = (await getDoc(orgsDocRef)).data() as Organisation;
          orgData.id = orgOwnId;
          return { data: [orgData] };
        }
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Organisation' as const, id })),
              { type: 'Organisation', id: 'LIST' },
            ]
          : [{ type: 'Organisation', id: 'LIST' }],
    }),
    getEventStatsRealtime: builder.query<EventStats, string>({
      queryFn: () => ({
        data: {
          totalOwnerShare: 0,
          totalSales: 0,
          totalTickets: 0,
          promoCodeStats: {},
        },
      }),
      async onCacheEntryAdded(eventId, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        try {
          await cacheDataLoaded;

          const unsubscribe = onSnapshot(doc(DB, 'events', eventId), (docSnapshot) => {
            if (docSnapshot.exists()) {
              const eventData = docSnapshot.data();
              updateCachedData(
                (draft): EventStats => ({
                  totalOwnerShare: Number(eventData.totalOwnerShare) || 0,
                  totalSales: Number(eventData.totalSales) || 0,
                  totalTickets: Number(eventData.totalTickets) || 0,
                  promoCodeStats: eventData.promoCodeStats || {},
                })
              );
            }
          });

          await cacheEntryRemoved;
          unsubscribe();
        } catch (error) {
          console.error('Error in real-time subscription:', error);
        }
      },
      providesTags: (result, error, eventId) => [
        { type: 'Event' as const, id: eventId },
        { type: 'EventStats' as const, id: eventId },
      ],
    }),
    fetchOrg: builder.query<Organisation, string>({
      async queryFn(orgId) {
        let orgsDocRef = doc(DB, 'organisers', orgId);
        const orgData = (await getDoc(orgsDocRef)).data() as Organisation;
        orgData.id = orgId;

        return { data: orgData };
      },
      providesTags: (result, error, orgId) => [{ type: 'Organisation', id: orgId }],
    }),
    // User
    fetchUser: builder.query<UserRequest, string>({
      async queryFn(userId) {
        let userDocRef = doc(DB, 'users', userId);
        let userData = (await getDoc(userDocRef)).data() as UserRequest;
        return { data: userData };
      },
      providesTags: (result, error, userId) => [{ type: 'User', id: userId }],
    }),
    // tickets
    fetchTickets: builder.query<Ticket[], string>({
      async queryFn(eventId) {
        let ticketsRef = collection(DB, 'events', eventId, 'tickets');
        const qTickets = query(ticketsRef, where('eventId', '==', eventId));
        let ticketsData: Ticket[] = (await getDocs(qTickets)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          //SellTimeLimit has start and end as Timestamps so we need to convert them to Date
          SellTimeLimit: {
            start: doc.data().SellTimeLimit?.start
              ? (doc.data().SellTimeLimit?.start as Timestamp).toDate()
              : undefined,
            end: doc.data().SellTimeLimit?.end
              ? (doc.data().SellTimeLimit?.end as Timestamp).toDate()
              : undefined,
          },
          // accessTimeThreshold: {
          //   start: doc.data().accessTimeThreshold?.start
          //     ? (doc.data().accessTimeThreshold?.start as Timestamp).toDate()
          //     : undefined,
          //   end: doc.data().accessTimeThreshold?.end
          //     ? (doc.data().accessTimeThreshold?.end as Timestamp).toDate()
          //     : undefined,
          // },
        }));
        //console.log(ticketsData);
        return { data: ticketsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Ticket', id: 'LIST' },
      ],
    }),
    // Reservations
    fetchEventReservations: builder.query<Reservation[], string>({
      async queryFn(eventId) {
        let reservationsRef = collection(DB, 'reservations');
        const qResa = query(reservationsRef, where('eventid', '==', eventId));
        const qTickets = query(collectionGroup(DB, 'tickets'), where('eventid', '==', eventId));
        let reservationticketsData: ReservationTicket[] = (await getDocs(qTickets)).docs.map(
          (doc: any) => ({ id: doc.id, ...doc.data() })
        );
        let reservationsData: Reservation[] = (await getDocs(qResa)).docs.map((doc: any) => {
          let reservation = doc.data() as Reservation;
          let tickts = reservationticketsData?.filter((tkt) => tkt.reservationid === doc.id);
          let totalamount = tickts.reduce(function (acc, obj) {
            return acc + obj.price;
          }, 0);
          console.log(reservation.createdAt, reservation.updatedAt);
          /* let boughtAt = reservation?.boughtAt
            ? ((reservation?.boughtAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0); */
          console.log(reservation?.event?.startsAt);

          let createdAt = reservation?.createdAt
            ? ((reservation?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0);
          let updatedAt = reservation?.updatedAt
            ? ((reservation?.updatedAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0);
          /* let eventStartAt = reservation?.event?.startsAt
            ? ((reservation?.event?.startsAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0); */
          console.log(createdAt, updatedAt);
          return {
            id: reservation?.id,
            ...doc.data(),
            boughtAt: createdAt,
            tickets: tickts,
            totalAmount: totalamount,
            createdAt: createdAt,
            updatedAt: updatedAt,
          };
        });
        console.log('reservationsData ' + JSON.stringify(reservationsData));
        return { data: reservationsData };
      },
    }),
    fetchEventReservationsTickets: builder.query<ReservationTicket[], string>({
      async queryFn(eventId) {
        let reservationsRef = collection(DB, 'reservations');
        const qResa = query(reservationsRef, where('eventid', '==', eventId));
        const qTickets = query(collectionGroup(DB, 'tickets'), where('eventid', '==', eventId));
        let reservationsData: Reservation[] = (await getDocs(qResa)).docs.map((doc: any) => {
          let reservation = doc.data() as Reservation;
          let createdAt = reservation?.createdAt
            ? ((reservation?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0);
          let updatedAt = reservation?.updatedAt
            ? ((reservation?.updatedAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0);
          let boughtAt = reservation?.boughtAt
            ? ((reservation?.boughtAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0);
          /* let eventStartAt = reservation?.event?.startsAt
            ? ((reservation?.event?.startsAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0); */
          return {
            id: doc.id,
            ...doc.data(),

            boughtAt: boughtAt,
            createdAt: createdAt,
            updatedAt: updatedAt,
          };
        });
        console.log(reservationsData);
        let reservationticketsData: ReservationTicket[] = (await getDocs(qTickets)).docs.map(
          (doc: any) => {
            console.log(doc.data());
            let reservationT = reservationsData.filter(
              (resa) => resa.id === doc.data().reservationid
            )[0] as Reservation;
            return {
              id: doc.id,
              reservation: reservationT,
              ...doc.data(),
              createdAt: doc.data().createdAt
                ? ((doc.data().createdAt as Timestamp).toDate() as EventDate)
                : addDays(Date.now(), 0),
              updatedAt: doc.data().updatedAt
                ? ((doc.data().updatedAt as Timestamp).toDate() as EventDate)
                : addDays(Date.now(), 0),
              boughtAt: doc.data().createdAt
                ? ((doc.data().createdAt as Timestamp).toDate() as EventDate)
                : addDays(Date.now(), 0),
              //client: reservationT.client,
            };
          }
        );
        console.log('reservationticketsData  ' + JSON.stringify(reservationticketsData));
        return { data: reservationticketsData };
      },
    }),
    fetchEventAttendees: builder.query<Client[], string>({
      async queryFn(eventId) {
        let clientsRef = collection(DB, 'events', eventId, 'attendees');
        let clientsData: Client[] = (await getDocs(clientsRef)).docs.flatMap((doc: any) => {
          return doc.data().hasMultiPersonsTickets ? [...doc.data().participants] : [doc.data()];
        });
        //console.log(clientsData);
        return { data: clientsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    // ticket mutations
    addTicket: builder.mutation<Ticket, Ticket>({
      async queryFn(ticket) {
        let ticketsRef = collection(DB, 'events', ticket.eventId, 'tickets');
        let docId = '';
        await addDoc(ticketsRef, ticket).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        let newTicket = { ...ticket, id: docId };
        return { data: newTicket };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    addBasicticket: builder.mutation<TicketBasicResponse, TicketBasicRequest>({
      async queryFn(args) {
        let { id, startDate, endDate } = args;
        let ticket = { ...basicTicket, eventId: id, startDate: startDate, endDate: endDate };
        let ticketsRef = collection(DB, 'events', args.id!, 'tickets');
        await addDoc(ticketsRef, ticket);
        return { data: ticket };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    editTicket: builder.mutation<Ticket, Ticket>({
      async queryFn(ticket) {
        const ticketId = ticket.id;
        delete ticket.id;
        //let ticketsRef = collection(DB, 'events', ticket.eventId, 'tickets');
        let ticketDocRef = doc(DB, 'events', ticket.eventId, 'tickets', ticketId!);
        await updateDoc(ticketDocRef, ticket);
        let newTicket = { ...ticket, id: ticketId };
        return { data: newTicket };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    removeTicket: builder.mutation<string, { eventId: string; ticketId: string }>({
      async queryFn({ eventId, ticketId }) {
        const ticketDocRef = doc(DB, 'events', eventId, 'tickets', ticketId!);

        let isPassCultureTicket = false;
        try {
          const ticketSnap = await getDoc(ticketDocRef);
          const categoryId = ticketSnap.data()?.categoryId;
          if (categoryId) {
            const categorySnap = await getDoc(
              doc(DB, 'events', eventId, 'ticketCategories', categoryId)
            );
            isPassCultureTicket = categorySnap.data()?.isPassCulture === true;
          }
        } catch (err) {
          console.warn('Failed to resolve ticket category for Pass Culture check:', err);
        }

        if (isPassCultureTicket) {
          try {
            const deletePassCultureStock = httpsCallable(
              FUNCTIONS,
              'passculture-deletePassCultureStockCallable'
            );
            await deletePassCultureStock({ eventId, ticketId });
          } catch (err) {
            console.warn('Pass Culture stock deletion failed:', err);
          }
        }

        await deleteDoc(ticketDocRef);
        return { data: ticketId };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    // event mutations
    addEvent: builder.mutation<Partial<EventType>, Partial<EventType>>({
      async queryFn(args) {
        let {
          title,
          summary,
          startDate,
          endDate,
          is_recurring,
          organisation,
          ownerid,
          creationStatus,
          creationStep,
          totalSales,
          totalTickets,
        } = args;
        let eventsRef = collection(DB, 'events');
        let docId = '';
        await addDoc(eventsRef, {
          ownerid,
          title,
          summary,
          startDate,
          endDate,
          is_recurring,
          eventTimeZoneUtc: 44,
          createdAt: new Date(),
          organisation,
          creationStatus,
          creationStep,
          totalSales,
          totalTickets,
        }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    editEvent: builder.mutation<EventType, any>({
      async queryFn({ eventId, event }) {
        console.log(event);
        delete event.id;
        let eventDocRef = doc(DB, 'events', eventId);
        updateDoc(eventDocRef, {
          ...event,
          tickets: undefined,
          org: undefined,
        });
        return { data: event };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    updateEvent: builder.mutation<void, { id: string; [key: string]: any }>({
      async queryFn(arg) {
        const { id, ...updateData } = arg;
        try {
          const eventRef = doc(DB, 'events', id);
          await updateDoc(eventRef, updateData);
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    publishEvent: builder.mutation<boolean, string>({
      async queryFn(id) {
        console.log(id);
        let eventDocRef = doc(DB, 'events', id!);
        updateDoc(eventDocRef, {
          creationStatus: 'published',
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    creationStepEvent: builder.mutation<boolean, { eventId: string; step: string }>({
      async queryFn({ eventId, step }) {
        console.log(eventId);
        let eventDocRef = doc(DB, 'events', eventId!);
        updateDoc(eventDocRef, {
          creationStep: step,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: arg.eventId }],
    }),
    createEventLink: builder.mutation<boolean, { id: string; ownerid?: string }>({
      async queryFn({ id, ownerid }) {
        console.log(id);
        let data = {
          id: id,
          eventId: id,
          tier: 'event',
          format: 'SHORT',
          useNewLinkStructure: true,
        };

        const createDeepLink = httpsCallable(FUNCTIONS, 'deeplinks-createEventDeepLink');
        const result = await createDeepLink(data);
        console.log('create callable link', result.data);
        const deeplink = result?.data as DeepLinkResponse;
        console.log(deeplink);

        return { data: !deeplink.hasError };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    cancelEvent: builder.mutation<boolean, string>({
      async queryFn(id) {
        console.log(id);
        let eventDocRef = doc(DB, 'events', id!);
        updateDoc(eventDocRef, {
          creationStatus: 'canceled',
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    archiveEvent: builder.mutation<boolean, string>({
      async queryFn(id) {
        console.log(id);
        let eventDocRef = doc(DB, 'events', id!);
        updateDoc(eventDocRef, {
          creationStatus: 'archived',
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    editEventCover: builder.mutation<UpdateEventCoverResponse, UpdateEventCoverRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, coverImage } = args;
        let eventDocRef = doc(DB, 'events', id);
        updateDoc(eventDocRef, {
          coverImage: coverImage,
        });
        return { data: { coverImage } };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    editEventInvoiceInfos: builder.mutation<
      UpdateEventInvoiceInfosRequest,
      UpdateEventInvoiceInfosRequest
    >({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, isInvoiceRequred, invoiceInfo } = args;
        let eventDocRef = doc(DB, 'events', id);
        updateDoc(eventDocRef, {
          isInvoiceRequred: isInvoiceRequred,
          invoiceInfo: invoiceInfo,
        });
        return { data: { id, isInvoiceRequred, invoiceInfo } };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    removeEvent: builder.mutation<boolean, string>({
      async queryFn(eventId) {
        let eventDocRef = doc(DB, 'events', eventId);
        await deleteDoc(eventDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    editEvntOrg: builder.mutation<EventType, { event: EventType }>({
      async queryFn({ event }) {
        let eventDocRef = doc(DB, 'events', event.id);
        setDoc(
          eventDocRef,
          { ...event, organisation: event.organisation, tickets: undefined, org: undefined },
          { merge: true }
        );
        return { data: event };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg.event.id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    // org mutations
    addOrg: builder.mutation<Organisation, Partial<Organisation>>({
      async queryFn(args) {
        let {
          ownerid,
          name,
          type,
          bio,
          coverImage,
          logoImage,
          contact,
          followers,
          totalEvents,
          address,
          latitude,
          longitude,
          // Enhanced fields for recording studios
          settings,
          equipmentIds,
          servicesOffered,
          language,
          // Studio business settings
          isPubliclyVisible,
          isBookingOpen,
          // Timestamps
          createdAt,
          updatedAt,
          isDeleted,
        } = args;

        console.log(contact);
        let orgsRef = collection(DB, 'organisers');

        let docId = '';

        // Prepare org data with conditional enhanced fields
        const orgData: any = {
          ownerid,
          name,
          type,
          bio,
          coverImage,
          logoImage,
          contact,
          followers,
          totalEvents,
          address,
          latitude,
          longitude,
          // Timestamps and status
          createdAt,
          updatedAt,
          isDeleted,
        };

        // Add enhanced fields for recording studios
        if (type === 'recording_studio') {
          if (settings) orgData.settings = settings;
          if (equipmentIds) orgData.equipmentIds = equipmentIds;
          if (servicesOffered) orgData.servicesOffered = servicesOffered;
          if (language) orgData.language = language;
          // Studio business settings - default to false for safety
          orgData.isPubliclyVisible = isPubliclyVisible ?? false;
          orgData.isBookingOpen = isBookingOpen ?? false;
        }

        await addDoc(orgsRef, orgData).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });

        // Add the organization ID to the owner's orgs array
        if (ownerid && docId) {
          try {
            const userRef = doc(DB, 'users', ownerid);
            await updateDoc(userRef, {
              orgs: arrayUnion(docId),
            });
            console.log('Added org ID to user orgs array:', docId);
          } catch (error) {
            console.error('Failed to update user orgs array:', error);
            // Don't fail the whole operation if this fails
          }
        }

        return { data: { ...args, id: docId } as Organisation };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Organisation', id: 'LIST' }],
    }),
    addFullOrg: builder.mutation<Organisation, Organisation>({
      async queryFn(org) {
        const { name, type, bio, coverImage, logoImage } = org;

        if (!name || !type || !bio || !coverImage || !logoImage) {
          return {
            error: {
              message: 'Missing required fields',
              data: { name, type, bio, coverImage, logoImage },
            },
          };
        }

        try {
          const addOrg = httpsCallable(FUNCTIONS, 'addOrg');
          const result = await addOrg(org);

          if (result?.data) {
            return { data: result.data as Organisation };
          }

          return {
            error: {
              message: `Failed to create new org: ${name}`,
              data: org,
            },
          };
        } catch (error: any) {
          return {
            error: {
              message: error.message || 'An error occurred while creating organization',
              data: error,
            },
          };
        }
      },
      invalidatesTags: [{ type: 'Organisation', id: 'LIST' }],
    }),
    editOrg: builder.mutation<Organisation, any>({
      async queryFn(org) {
        console.log('editOrg mutation received:', org);
        const { id, ...updateData } = org;
        console.log('Updating org document with:', updateData);
        let eventDocRef = doc(DB, 'organisers', id!);
        await updateDoc(eventDocRef, updateData);
        return { data: org };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Organisation', id: arg.id },
        { type: 'Organisation', id: 'LIST' },
      ],
    }),
    updateEventAccessRestriction: builder.mutation<
      boolean,
      { eventId: string; accessRestriction: { description: string; disclaimer: string } }
    >({
      async queryFn({ eventId, accessRestriction }) {
        try {
          const eventRef = doc(DB, 'events', eventId);

          // Update only the accessRestriction field in the event document
          await updateDoc(eventRef, {
            accessRestriction: accessRestriction,
            updatedAt: serverTimestamp(),
          });

          return { data: true };
        } catch (error) {
          console.error('Error updating access restriction:', error);
          return { error: { message: 'Failed to update access restriction' } };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'Events', id: eventId }],
    }),
    editOrgCover: builder.mutation<UpdateEventCoverResponse, UpdateEventCoverRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, coverImage } = args;
        let orgDocRef = doc(DB, 'organisers', id);
        updateDoc(orgDocRef, {
          coverImage: coverImage,
        });
        return { data: { coverImage } };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Organisation', id: arg.id },
        { type: 'Organisation', id: 'LIST' },
      ],
    }),
    editOrgLogo: builder.mutation<UpdateEventLogoResponse, UpdateEventLogoRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, logoImage } = args;
        let orgDocRef = doc(DB, 'organisers', id);
        updateDoc(orgDocRef, {
          logoImage: logoImage,
        });
        return { data: { logoImage } };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Organisation', id: arg.id },
        { type: 'Organisation', id: 'LIST' },
      ],
    }),
    removeOrg: builder.mutation<string, string>({
      async queryFn(orgId) {
        let orgDocRef = doc(DB, 'organisers', orgId);
        updateDoc(orgDocRef, {
          isDeleted: true,
        });
        return { data: orgId };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Organisation', id: 'LIST' },
        { type: 'Organisation', id: arg },
      ],
    }),
    // user mutations
    editUserStatus: builder.mutation<boolean, string>({
      async queryFn(userid) {
        let userDocRef = doc(DB, 'users', userid);
        updateDoc(userDocRef, {
          isNewUser: false,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg }],
    }),
    editUser: builder.mutation<boolean, UserRequest>({
      async queryFn(user) {
        let userDocRef = doc(DB, 'users', user.uid);
        updateDoc(userDocRef, {
          ...user,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg.uid }],
    }),
    editUserProfilePicture: builder.mutation<UpdateEventCoverResponse, UpdateEventCoverRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, coverImage } = args;
        let eventDocRef = doc(DB, 'users', id);
        updateDoc(eventDocRef, {
          photoURL: coverImage,
        });
        return { data: { coverImage } };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg.id }],
    }),
    editUserAccountId: builder.mutation<boolean, UpdateEventAccountIdRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, accountId } = args;
        let eventDocRef = doc(DB, 'users', id);
        updateDoc(eventDocRef, {
          stripeConnect: {
            accountId: accountId,
            status: 'inactive',
          },
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'User', id: arg.id }],
    }),
    editOrgAccountId: builder.mutation<boolean, UpdateEventAccountIdRequest>({
      async queryFn(args, queryApi, extraOptions, baseQuery) {
        let { id, accountId } = args;
        let orgDocRef = doc(DB, 'organisers', id);
        updateDoc(orgDocRef, {
          accountId: accountId,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Organisation', id: arg.id }],
    }),
    fetchEventReferrals: builder.query<UpdatePromotersRequest[], string>({
      async queryFn(eventId) {
        let referralsRef = collection(DB, 'events', eventId, 'referrals');
        let referralsData: UpdatePromotersRequest[] = (await getDocs(referralsRef)).docs.map(
          (doc: any) => {
            return {
              id: doc.id,
              ...doc.data(),
            };
          }
        );
        console.log(referralsData);
        return { data: referralsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    addReferrals: builder.mutation<UpdatePromotersRequest, UpdatePromotersRequest>({
      async queryFn(referral) {
        let referralsRef = collection(DB, 'events', referral.eventid, 'referrals');
        let referralid = '';
        let createdAt = new Date();
        let data = {
          id: referral.eventid,
          tier: 'event',
          format: 'SHORT',
          referralid: referral.referralid,
          useNewLinkStructure: true,
        };

        const createDeepLink = httpsCallable(FUNCTIONS, 'createDeepLink');
        const result = await createDeepLink(data);
        console.log('create callable link', result);

        const deeplink = result?.data as DeepLinkResponse;

        await addDoc(referralsRef, {
          ...referral,
          createdAt: createdAt,
          link: deeplink.newdoc,
        }).then((docRef) => {
          console.log('new ref Document written with ID: ', docRef.id);
          referralid = docRef.id;
        });
        console.log('after ref creation');

        let newReferral = { ...referral, id: referralid };
        return { data: newReferral };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),

    removePromoter: builder.mutation<boolean, { eventId: string; referralid: string }>({
      async queryFn({ eventId, referralid }) {
        let referralDocRef = doc(DB, 'events', eventId, 'referrals', referralid);
        await deleteDoc(referralDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    addStore: builder.mutation<Partial<StoreType>, Partial<StoreType>>({
      async queryFn(args) {
        let {
          name,
          description,
          image,
          address,
          phone,
          prepTime,
          ownerid,
          category,
          time,
          status,
          rate,
          review,
          closingTime,
        } = args;
        let storesRef = collection(DB, 'stores');
        let docId = '';
        await addDoc(storesRef, {
          ownerid,
          name,
          description,
          image,
          address,
          phone,
          prepTime,
          category,
          time,
          status,
          rate,
          review,
          closingTime,
        }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Store', id: 'LIST' }],
    }),
    fetchStores: builder.query<Store[], string>({
      async queryFn(authId) {
        const constraints = [];
        constraints.push(where('ownerid', '==', authId));
        let storesRef = collection(DB, 'stores');
        const qStore = query(storesRef, ...constraints);
        let storeData: Store[] = (await getDocs(qStore)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : undefined,
        }));
        return { data: storeData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Store' as const, id })),
              { type: 'Store', id: 'LIST' },
            ]
          : [{ type: 'Store', id: 'LIST' }],
    }),
    editStore: builder.mutation<boolean, StoreRequest>({
      async queryFn(store) {
        let storeDocRef = doc(DB, 'stores', store.id);
        setDoc(storeDocRef, { ...store });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Store', id: arg.id }],
    }),
    editStoreCoverPicture: builder.mutation<UpdateStoreCoverResponse, UpdateStoreCoverRequest>({
      async queryFn(args) {
        let { id, image } = args;
        let storeDocRef = doc(DB, 'stores', id);
        updateDoc(storeDocRef, {
          image: image,
        });
        return { data: { image } };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Store', id: arg.id }],
    }),
    addRoom: builder.mutation<Partial<RoomType>, Partial<RoomType>>({
      async queryFn(args) {
        let { roomNumber, orgId, name } = args;
        let roomsRef = collection(DB, 'rooms');
        let docId = '';
        await addDoc(roomsRef, { roomNumber, orgId, name }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Room', id: 'LIST' }],
    }),
    removeRoom: builder.mutation<boolean, string>({
      async queryFn(roomId) {
        let roomDocRef = doc(DB, 'rooms', roomId);
        deleteDoc(roomDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Room', id: 'LIST' }],
    }),
    fetchRooms: builder.query<RoomType[], string>({
      async queryFn(orgId) {
        const constraints = [];
        constraints.push(where('orgId', '==', orgId));
        let roomsRef = collection(DB, 'rooms');
        const qRoom = query(roomsRef, ...constraints);
        let roomData: RoomType[] = (await getDocs(qRoom)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : undefined,
          tables: [] as any,
        }));
        for (let i = 0; i < roomData.length; i++) {
          const room = roomData[i];
          const tablesRef = collection(DB, 'rooms', room.id, 'tables');
          const qTable = query(tablesRef);
          const tableData = (await getDocs(qTable)).docs.map((doc: any) => ({
            id: doc.id,
            ...doc.data(),
          }));
          room.tables = tableData;
        }
        return { data: roomData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Room' as const, id })),
              { type: 'Room', id: 'LIST' },
            ]
          : [{ type: 'Room', id: 'LIST' }],
    }),
    addTable: builder.mutation<
      Partial<TableType>,
      { roomId: string; tableNumber: number; qrcode: string; places: number }
    >({
      async queryFn(args) {
        let { roomId, tableNumber, qrcode, places } = args;
        const tablesRef = collection(DB, 'rooms', roomId, 'tables');
        let docId = '';
        await addDoc(tablesRef, { tableNumber, qrcode, places }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Room', id: 'LIST' }],
    }),
    removeTable: builder.mutation<boolean, { roomId: string; tableId: string }>({
      async queryFn(args) {
        const { roomId, tableId } = args;
        const tablesRef = collection(DB, 'rooms', roomId, 'tables');
        await deleteDoc(doc(tablesRef, tableId));
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Room', id: 'LIST' }],
    }),
    fetchProducts: builder.query<ProductType[], string>({
      async queryFn(storeId) {
        const constraints = [];
        constraints.push(where('storeid', '==', storeId));
        let productsRef = collection(DB, 'products');
        const qProduct = query(productsRef, ...constraints);
        let productData: ProductType[] = (await getDocs(qProduct)).docs.map((doc: any) => ({
          id: doc.id,
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : undefined,
          ...doc.data(),
        }));
        return { data: productData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Product' as const, id })),
              { type: 'Product', id: 'LIST' },
            ]
          : [{ type: 'Product', id: 'LIST' }],
    }),
    addProduct: builder.mutation<Partial<ProductType>, Partial<ProductType>>({
      async queryFn(args) {
        let {
          name,
          description,
          image,
          categoryid,
          isalcoholic,
          iscomposite,
          price,
          storeid,
          hs,
          tier,
          composition,
          remote_id,
          quantity,
        } = args;
        let productsRef = collection(DB, 'products');
        let docId = '';
        await addDoc(productsRef, {
          name,
          description,
          image,
          categoryid,
          isalcoholic,
          iscomposite,
          price,
          storeid,
          hs,
          tier,
          composition,
          remote_id,
          quantity,
        }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    fetchProductCategories: builder.query<ProductCategoryType[], string>({
      async queryFn(storeId) {
        const constraints = [];
        constraints.push(where('storeid', '==', storeId));
        let categorisRef = collection(DB, 'productcategories');
        const qCategory = query(categorisRef, ...constraints);
        let categoryData: ProductCategoryType[] = (await getDocs(qCategory)).docs.map(
          (doc: any) => ({
            id: doc.id,
            createdAt: doc.data()?.createdAt
              ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
              : undefined,
            ...doc.data(),
          })
        );
        return { data: categoryData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Category' as const, id })),
              { type: 'Category', id: 'LIST' },
            ]
          : [{ type: 'Category', id: 'LIST' }],
    }),
    editProduct: builder.mutation<boolean, ProductRequest>({
      async queryFn(product) {
        let productDocRef = doc(DB, 'products', product.id);
        setDoc(productDocRef, { ...product });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: arg.id }],
    }),
    removeProduct: builder.mutation<boolean, string>({
      async queryFn(productId) {
        let productDocRef = doc(DB, 'products', productId);
        deleteDoc(productDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    removeStore: builder.mutation<boolean, string>({
      async queryFn(storeId) {
        let storeDocRef = doc(DB, 'stores', storeId);
        deleteDoc(storeDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    addCategory: builder.mutation<
      Partial<ProductCategoryType>,
      { storeid: string; name: string; order: number }
    >({
      async queryFn(args) {
        let { storeid, name, order } = args;
        const categoriesRef = collection(DB, 'productcategories');
        let docId = '';
        await addDoc(categoriesRef, { storeid, name, order }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Category', id: 'LIST' }],
    }),
    fetchOrders: builder.query<OrderType[], string>({
      async queryFn(storeId) {
        const constraints = [];
        constraints.push(where('storeid', '==', storeId));
        let ordersRef = collection(DB, 'orders');
        const qOrder = query(ordersRef, ...constraints);
        let orderData: OrderType[] = (await getDocs(qOrder)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt:
            doc.data()?.createdAt && typeof doc.data()?.createdAt !== 'string'
              ? (doc.data()?.createdAt as Timestamp).toDate()
              : doc.data()?.createdAt,
        }));
        return { data: orderData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Order' as const, id })),
              { type: 'Order', id: 'LIST' },
            ]
          : [{ type: 'Order', id: 'LIST' }],
    }),
    addOrder: builder.mutation<Partial<OrderType>, Partial<OrderType>>({
      async queryFn(args) {
        let ordersRef = collection(DB, 'orders');
        let docId = '';
        await addDoc(ordersRef, args).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Order', id: 'LIST' }],
    }),
    editOrder: builder.mutation<boolean, OrderRequest>({
      async queryFn(order) {
        let orderDocRef = doc(DB, 'orders', order.id);
        setDoc(orderDocRef, { ...order });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Order', id: arg.id }],
    }),
    removeOrder: builder.mutation<boolean, string>({
      async queryFn(orderId) {
        let orderDocRef = doc(DB, 'orders', orderId);
        deleteDoc(orderDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Order', id: 'LIST' }],
    }),
    fetchNotifications: builder.query<NotificationType[], string>({
      async queryFn(authId) {
        const constraints = [];
        constraints.push(where('ownerid', '==', authId));
        let notificationsRef = collection(DB, 'notifications');
        const qNotification = query(notificationsRef, ...constraints);
        let notificationData: NotificationType[] = (await getDocs(qNotification)).docs.map(
          (doc: any) => ({
            id: doc.id,
            ...doc.data(),
          })
        );
        return { data: notificationData };
      },
      providesTags: (result, error, authId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Notification' as const, id })),
              { type: 'Notification', id: 'LIST' },
            ]
          : [{ type: 'Notification', id: 'LIST' }],
    }),
    addNotification: builder.mutation<Partial<NotificationType>, Partial<NotificationType>>({
      async queryFn(args) {
        let notificationsRef = collection(DB, 'notifications');
        let docId = '';
        await addDoc(notificationsRef, args).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Notification', id: 'LIST' }],
    }),
    editNotification: builder.mutation<boolean, NotificationRequest>({
      async queryFn(notification) {
        let notificationDocRef = doc(DB, 'notifications', notification.id);
        setDoc(notificationDocRef, { ...notification });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Notification', id: arg.id }],
    }),

    fetchReferredEvents: builder.query<any, string>({
      async queryFn(email) {
        let events: any = [];

        try {
          const constraints = [];
          constraints.push(where('email', '==', email));

          constraints.push(where('status', '==', 'accepted'));

          const request = query(collectionGroup(DB, 'referrals'), ...constraints);

          let rrequestData = (await getDocs(request)).docs
            .map((doc: any) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .map((item) => item.eventid)
            .filter((value, index, array) => array.indexOf(value) === index);
          await Promise.all(
            rrequestData?.map(async (id) => {
              let eventsDocRef = doc(DB, 'events', id);
              let eventData = (await getDoc(eventsDocRef)).data() as EventType;
              if (eventData) {
                eventData.id = id;
                eventData.createdAt = eventData?.createdAt
                  ? ((eventData?.createdAt as Timestamp).toDate() as EventDate)
                  : addDays(Date.now(), 0);
                eventData.startDate = eventData?.startDate
                  ? ((eventData?.startDate as Timestamp).toDate() as EventDate)
                  : addDays(Date.now(), 0);
                eventData.endDate = eventData?.endDate
                  ? ((eventData?.endDate as Timestamp).toDate() as EventDate)
                  : addDays(Date.now(), 1);
                eventData.datesReaccuring = eventData?.datesReaccuring
                  ? ([
                      ...(eventData?.datesReaccuring?.map((date: any) => ({
                        start: date.start ? (date.start as Timestamp).toDate() : undefined,
                        end: date.end ? (date.end as Timestamp).toDate() : undefined,
                      })) || []),
                    ] as EventDateRange[])
                  : undefined;
                events.push(eventData);
              }
            })
          );
        } catch (e) {
          console.error(e);
        }

        return { data: events };
      },
    }),

    addRpRequest: builder.mutation<any, UpdateInvitationRequest>({
      async queryFn({ senderID, invitationData }) {
        let docId = '';
        let invitationDocRef = collection(DB, 'users', senderID, 'referralrequests');
        await addDoc(invitationDocRef, invitationData).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          data: { ...invitationData, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    cancelRpRequest: builder.mutation<boolean, deleteInvitationRequest>({
      async queryFn(invitationData) {
        const constraints = [];
        constraints.push(where('userID', '==', invitationData.senderID));
        let invitationRef = collection(DB, 'users', invitationData.userID, 'referralrequests');
        const invitationDocRef = query(invitationRef, ...constraints);
        let selectedInvitationData: any = (await getDocs(invitationDocRef)).docs.map(
          (doc: any) => ({
            id: doc.id,
            ...doc.data(),
          })
        );
        let selectedInvitationRef = doc(
          DB,
          'users',
          invitationData.userID,
          'referralrequests',
          selectedInvitationData[0].id
        );
        await deleteDoc(selectedInvitationRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    fetchPromotionRequests: builder.query<Request[], string>({
      async queryFn(ownerid) {
        const request = collection(DB, 'users', ownerid, 'referralrequests');

        let requestData: Request[] = (await getDocs(request)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return { data: requestData } as any;
      },
    }),
    acceptRequest: builder.mutation<boolean, any>({
      async queryFn({ userid, requestId, status }) {
        let requestDocRef = doc(DB, 'users', userid, 'referralrequests', requestId);
        updateDoc(requestDocRef, {
          status: status,
        });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    rejectRequest: builder.mutation<boolean, any>({
      async queryFn({ userid, requestId }) {
        let requestDocRef = doc(DB, 'users', userid, 'referralrequests', requestId);
        deleteDoc(requestDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'Event', id: arg },
        { type: 'Event', id: 'LIST' },
      ],
    }),
    fetchReferralRequest: builder.query<any, string>({
      async queryFn(referralId) {
        const request = query(
          collectionGroup(DB, 'referralrequests'),
          where('userID', '==', referralId)
        );
        let rrequestData: ReservationTicket[] = (await getDocs(request)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return { data: rrequestData };
      },
    }),
    fetchPromotersGroups: builder.query<any, string>({
      async queryFn(userID) {
        let groupsRef = collection(DB, 'users', userID, 'promotersGroup');
        let groupsData = (await getDocs(groupsRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        console.log(groupsData);
        return { data: groupsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    removePromotersGroup: builder.mutation<string, { userID: string; groupId: string }>({
      async queryFn({ userID, groupId }) {
        let groupDocRef = doc(DB, 'users', userID, 'promotersGroup', groupId);
        deleteDoc(groupDocRef);
        return { data: groupId };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    addRpInvitation: builder.mutation<any, any>({
      async queryFn(requestData) {
        let docId = '';
        let invitationDocRef = collection(DB, 'users', requestData.uid, 'referralInvitation');

        await addDoc(invitationDocRef, requestData).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });
        return {
          data: { ...requestData, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    fetchGroupRequests: builder.query<any, string>({
      async queryFn(userID) {
        let groupsRef = collection(DB, 'users', userID, 'referralInvitation');
        let groupsData = (await getDocs(groupsRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        console.log(groupsData);
        return { data: groupsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    fetchGroupRequestsByGroupId: builder.query<any, string>({
      async queryFn(groupId) {
        const request = query(
          collectionGroup(DB, 'referralInvitation'),
          where('groupid', '==', groupId)
        );
        let rrequestData = (await getDocs(request)).docs.map((doc: any) => ({
          ...doc.data(),
        }));

        return { data: rrequestData };
      },
    }),
    addEventMenu: builder.mutation<Partial<ProductType>, Partial<ProductType>>({
      async queryFn(args) {
        let {
          name,
          description,
          image,
          categoryid,
          isalcoholic,
          iscomposite,
          price,
          storeid,
          hs,
          tier,
          composition,
          remote_id,
          eventid,
          quantity,
        } = args;
        let eventsRef = collection(DB, 'events');
        let eventDocRef = doc(eventsRef, eventid);

        // Assuming you want to create an "eventMenu" subcollection inside the event document
        let eventMenuRef = collection(eventDocRef, 'eventMenu');

        let docId = '';
        await addDoc(eventMenuRef, {
          name,
          description,
          image,
          categoryid,
          isalcoholic,
          iscomposite,
          price,
          storeid,
          hs,
          tier,
          composition,
          remote_id,
          quantity,
        }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });

        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    fetchEventMenu: builder.query<any, string>({
      async queryFn(eventID) {
        let eventMenuRef = collection(DB, 'events', eventID, 'eventMenu');
        let eventMenuData = (await getDocs(eventMenuRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        return { data: eventMenuData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    addEventProductCategory: builder.mutation<
      Partial<EventProductCategories>,
      Partial<EventProductCategories>
    >({
      async queryFn(args) {
        let { name, id, storeid, order, eventid } = args;
        let eventsRef = collection(DB, 'events');
        let eventDocRef = doc(eventsRef, eventid);

        // Assuming you want to create an "eventMenu" subcollection inside the event document
        let eventMenuRef = collection(eventDocRef, 'productsCategories');

        let docId = '';
        await addDoc(eventMenuRef, {
          name,
          id,
          storeid,
          order,
          eventid,
        }).then((docRef) => {
          console.log('Document written with ID: ', docRef.id);
          docId = docRef.id;
        });

        return {
          // get doc id from the result of the mutation
          data: { ...args, id: docId },
        };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: 'LIST' }],
    }),
    fetchEventProdCategories: builder.query<any, string>({
      async queryFn(eventID) {
        let eventMenuRef = collection(DB, 'events', eventID, 'productsCategories');
        let eventMenuData = (await getDocs(eventMenuRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        return { data: eventMenuData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    editEventProduct: builder.mutation<boolean, ProductRequest>({
      async queryFn(product) {
        const eventsRef = collection(DB, 'events');
        const eventDocRef = doc(eventsRef, product.eventid);
        const eventMenuDocRef = doc(eventDocRef, 'eventMenu', product.id);

        await updateDoc(eventMenuDocRef, { ...product });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Product', id: arg.id }],
    }),
    removeEventProduct: builder.mutation<boolean, { eventId: string; productId: string }>({
      async queryFn({ eventId, productId }) {
        let productDocRef = doc(DB, 'events', eventId, 'eventMenu', productId);
        await deleteDoc(productDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    fetchUserPublishedEvents: builder.query<EventType[], string>({
      async queryFn(userId) {
        const constraints = [];
        constraints.push(where('ownerid', '==', userId));
        constraints.push(where('creationStatus', '==', 'published'));
        let eventsRef = collection(DB, 'events');
        const qEvents = query(eventsRef, ...constraints);
        let eventsData: EventType[] = (await getDocs(qEvents)).docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data()?.createdAt
            ? ((doc.data()?.createdAt as Timestamp).toDate() as EventDate)
            : addDays(Date.now(), 0),
          startDate: doc.data()?.startDate
            ? (doc.data()?.startDate as Timestamp).toDate()
            : addDays(Date.now(), 0),
          endDate: doc.data()?.endDate
            ? (doc.data()?.endDate as Timestamp).toDate()
            : addDays(Date.now(), 1),
          datesReaccuring: doc.data()?.datesReaccuring
            ? ([
                ...(doc.data()?.datesReaccuring?.map((date: any) => ({
                  start: date.start ? (date.start as Timestamp).toDate() : undefined,
                  end: date.end ? (date.end as Timestamp).toDate() : undefined,
                })) || []),
              ] as EventDateRange[])
            : undefined,
        }));
        return { data: eventsData };
      },
      providesTags: (result, error, authId) =>
        result && result?.length > 0
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id: id })),
              { type: 'Event', id: 'LIST' },
            ]
          : [{ type: 'Event', id: 'LIST' }],
    }),
    editEventOrder: builder.mutation<boolean, OrderRequest>({
      async queryFn(order) {
        let orderDocRef = doc(DB, 'eventsOrders', order.id);
        setDoc(orderDocRef, { ...order });
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Order', id: arg.id }],
    }),
    removeEventOrder: builder.mutation<boolean, string>({
      async queryFn(orderId) {
        let orderDocRef = doc(DB, 'eventsOrders', orderId);
        deleteDoc(orderDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Order', id: 'LIST' }],
    }),
    fetchReservations: builder.query<any, string>({
      async queryFn(eventId) {
        try {
          const constraints = [];
          constraints.push(where('eventid', '==', eventId));

          constraints.push(where('status', '==', 'confirmed'));

          let reservationsRef = collection(DB, 'reservations');
          const qReservations = query(reservationsRef, ...constraints);

          let reservationsData = (await getDocs(qReservations)).docs.map((doc: any) => {
            return {
              id: doc.id,
              ...doc.data(),
            };
          });
          return { data: reservationsData } as any;
        } catch (e) {
          console.error(e);
        }
      },
    }),
    fetchEventDjs: builder.query<EventDjs[], string>({
      async queryFn(eventId) {
        let djsRef = collection(DB, 'events', eventId, 'djs');
        let djsData: EventDjs[] = (await getDocs(djsRef)).docs.map((doc: any) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        return { data: djsData };
      },
      providesTags: (result, error, eventId) => [
        //{ type: 'Event', id: eventId },
        { type: 'Client', id: 'LIST' },
      ],
    }),
    addDj: builder.mutation<EventDjs, EventDjs>({
      async queryFn(args) {
        let djsRef = collection(DB, 'events', args.eventid, 'djs');

        await addDoc(djsRef, {
          ...args,
        });

        return { data: { ...args } };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Ticket', id: 'LIST' }],
    }),
    removeDj: builder.mutation<boolean, { eventId: string; djid: string }>({
      async queryFn({ eventId, djid }) {
        let djDocRef = doc(DB, 'events', eventId, 'djs', djid);
        await deleteDoc(djDocRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    fetchTableProducts: builder.mutation<any[], { roomId: string; tableId: string }>({
      async queryFn({ roomId, tableId }) {
        let tableRef = doc(DB, 'rooms', roomId, 'tables', tableId);
        const productsRef = collection(tableRef, 'products');
        const productsSnapshot = await getDocs(productsRef);
        const productsList: any[] = productsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return { data: productsList };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    removeTableProduct: builder.mutation<
      boolean,
      { roomId: string; tableId: string; productId: string }
    >({
      async queryFn({ roomId, tableId, productId }) {
        let productRef = doc(DB, 'rooms', roomId, 'tables', tableId, 'products', productId);
        await deleteDoc(productRef);
        return { data: true };
      },
      invalidatesTags: (result, error, arg) => [{ type: 'Event', id: 'LIST' }],
    }),
    getMailCampaigns: builder.query<MailCampaign[], string>({
      async queryFn(eventId) {
        try {
          const campaignsRef = collection(DB, 'events', eventId, 'mailcampaigns');
          const querySnapshot = await getDocs(campaignsRef);
          const campaigns: MailCampaign[] = querySnapshot.docs.map((doc) => {
            //console.log(doc.data());
            //console.log(typeof doc.data().scheduledTime);
            return {
              id: doc.id,
              ...doc.data(),
            } as MailCampaign;
          });
          return { data: campaigns };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: (result, error, eventId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MailCampaign' as const, id })),
              { type: 'MailCampaign', id: 'LIST' },
            ]
          : [{ type: 'MailCampaign', id: 'LIST' }],
    }),

    // Get a single mail campaign
    getMailCampaign: builder.query<MailCampaign, { eventId: string; campaignId: string }>({
      async queryFn({ eventId, campaignId }) {
        try {
          const campaignRef = doc(DB, 'events', eventId, 'mailcampaigns', campaignId);
          const docSnap = await getDoc(campaignRef);
          if (docSnap.exists()) {
            return {
              data: {
                id: docSnap.id,
                ...docSnap.data(),
              } as MailCampaign,
            };
          } else {
            return { error: 'Mail campaign not found' };
          }
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: (result, error, arg) => [{ type: 'MailCampaign', id: arg.campaignId }],
    }),

    // Create a new mail campaign
    createMailCampaign: builder.mutation<MailCampaign, Omit<BaseCampaignFields, 'id'>>({
      async queryFn(campaign) {
        try {
          const campaignsRef = collection(DB, 'events', campaign.eventId, 'mailcampaigns');

          // Create campaign with required fields and defaults
          const newCampaignRef = await addDoc(campaignsRef, {
            ...campaign,
            status: 'draft',
            createdAt: new Date(),
            templateId: null,
            scheduledTime: null,
            launchType: null,
          });

          const newCampaign = await getDoc(newCampaignRef);

          return {
            data: {
              id: newCampaign.id,
              ...newCampaign.data(),
            } as MailCampaign,
          };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: [{ type: 'MailCampaign', id: 'LIST' }],
    }),
    editMailCampaign: builder.mutation<
      boolean,
      Partial<CompleteCampaignFields> & Pick<BaseCampaignFields, 'id' | 'eventId'>
    >({
      async queryFn(campaign) {
        try {
          const { id, eventId, ...updateData } = campaign;
          console.log(updateData);
          console.log('entering editMailCampaign');

          // Type guard to ensure required fields exist
          if (!id || !eventId) {
            throw new Error('Missing required id or eventId');
          }

          // Type guard to check for template-ready status transition
          const isTemplateReadyTransition =
            updateData.status === 'template-ready' &&
            'templateId' in updateData &&
            !updateData.templateId;

          // Type guard to check for scheduled status transition
          const isScheduledTransition =
            updateData.status === 'scheduled' &&
            'scheduledTime' in updateData &&
            !updateData.scheduledTime;

          // Prepare update data with status validations
          const finalUpdateData = {
            ...updateData,
            // Update status based on validations
            ...(isTemplateReadyTransition && {
              status: 'draft',
            }),
            ...(isScheduledTransition && {
              status: 'template-ready',
            }),
          };

          console.log('Updating campaign with data:', finalUpdateData);

          // Create document reference with type-safe path
          const campaignRef = doc(DB, 'events', eventId, 'mailcampaigns', id);
          await updateDoc(campaignRef, finalUpdateData);

          console.log('Campaign updated successfully');

          return { data: true };
        } catch (error: any) {
          console.error('Campaign update error:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'MailCampaign', id: arg.id },
        { type: 'MailCampaign', id: 'LIST' },
      ],
    }),
    // Update a mail campaign
    updateMailCampaign: builder.mutation<
      boolean,
      Partial<CompleteCampaignFields> & Pick<BaseCampaignFields, 'id' | 'eventId'>
    >({
      async queryFn(campaign) {
        try {
          const { id, eventId, ...updateData } = campaign;

          // Type guard to check for template-ready status transition
          const isTemplateReadyTransition =
            updateData.status === 'template-ready' &&
            'templateId' in updateData &&
            !updateData.templateId;

          // Type guard to check for scheduled status transition
          const isScheduledTransition =
            updateData.status === 'scheduled' &&
            'scheduledTime' in updateData &&
            !updateData.scheduledTime;

          // Prepare data for cloud function
          const data = {
            eventId,
            campaignId: id,
            updatedCampaign: {
              ...updateData,
              // Update status based on validations
              ...(isTemplateReadyTransition && {
                status: 'draft',
              }),
              ...(isScheduledTransition && {
                status: 'template-ready',
              }),
            },
          };

          console.log('Updating campaign with data:', data);

          const updateCallable = httpsCallable(FUNCTIONS, 'mailcampaigns-update');
          const result = await updateCallable(data);

          console.log('Update result:', result.data);

          return { data: true };
        } catch (error: any) {
          console.error('Campaign update error:', error);
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'MailCampaign', id: arg.id },
        { type: 'MailCampaign', id: 'LIST' },
      ],
    }),

    // Delete a mail campaign
    deleteMailCampaign: builder.mutation<boolean, Pick<MailCampaign, 'id' | 'eventId'>>({
      async queryFn({ id, eventId }) {
        try {
          let data = {
            eventId: eventId,
            campaignId: id,
          };
          const deleteCallable = httpsCallable(FUNCTIONS, 'mailcampaigns-delete');
          const result = await deleteCallable(data);
          console.log('create callable link', result.data);
          return { data: true };
        } catch (error: any) {
          console.log(error);
          return { error: error.message };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: 'MailCampaign', id: arg.id }],
    }),
    saveSeatmap: builder.mutation<void, { eventId: string; seatmapData: SeatMapData }>({
      async queryFn({ eventId, seatmapData }) {
        try {
          const eventRef = doc(DB, 'events', eventId);

          // Transform tables to include x and y properties
          const transformedTables = seatmapData.tables.map((table: Table) => {
            if (table.position && !('x' in table) && !('y' in table)) {
              return {
                ...table,
                x: table.position.x,
                y: table.position.y,
              };
            }
            return table;
          });

          const transformedSeatmapData = {
            ...seatmapData,
            tables: transformedTables,
          };

          await updateDoc(eventRef, { seatMap: transformedSeatmapData });
          return { data: undefined };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      invalidatesTags: ['SeatMap'],
    }),
    loadSeatmap: builder.query<SeatMapData | null, string>({
      async queryFn(eventId) {
        try {
          const eventRef = doc(DB, 'events', eventId);
          const eventDoc = await getDoc(eventRef);
          if (eventDoc.exists()) {
            const data = eventDoc.data().seatmap;
            if (data) {
              // Transform tables to include position field
              const transformedTables = data.tables.map(
                (table: Table & { x?: number; y?: number }) => {
                  if (table.x !== undefined && table.y !== undefined) {
                    const { x, y, ...rest } = table;
                    return {
                      ...rest,
                      position: { x, y } as Point,
                    };
                  }
                  return table;
                }
              );

              const transformedData: SeatMapData = {
                ...data,
                tables: transformedTables,
              };

              return { data: transformedData };
            }
          }
          return { data: null };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: ['SeatMap'],
    }),
    updateEventSalesStatus: builder.mutation<void, { eventId: string; closedForSales: boolean }>({
      async queryFn({ eventId, closedForSales }) {
        try {
          const eventRef = doc(DB, 'events', eventId);
          await updateDoc(eventRef, {
            closedForSales,
            updatedAt: serverTimestamp(),
          });
          return { data: undefined };
        } catch (error: any) {
          console.error('Error updating event sales status:', error);
          return {
            error: {
              status: error?.code || 'UNKNOWN_ERROR',
              data: error?.message || 'An unknown error occurred',
            },
          };
        }
      },
      // Optimistically update the cache
      async onQueryStarted({ eventId, closedForSales }, { dispatch, queryFulfilled }) {
        // Optimistically update the fetchEvent cache
        const patchResult = dispatch(
          EventsApi.util.updateQueryData('fetchEvent', eventId, (draft) => {
            draft.closedForSales = closedForSales;
          })
        );

        try {
          await queryFulfilled;
        } catch {
          // If the mutation fails, revert the optimistic update
          patchResult.undo();
        }
      },
      // Also invalidate the cache to ensure fresh data
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'Event', id: eventId },
        { type: 'Events', id: eventId },
      ],
    }),
  }),
});
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useFetchEventsWithOrgQuery,
  useFetchEventsQuery,
  useFetchEventsByOrgQuery,
  useFetchAllEventsQuery,
  useFetchEventQuery,
  useFetchOrgsQuery,
  useFetchReferredEventsQuery,
  useFetchTicketsQuery,
  useFetchEventsWithOrgAndTicketsQuery,
  useFetchEventWithTicketsQuery,
  useFetchOrgQuery,
  useFetchUserQuery,
  useFetchEventReservationsQuery,
  useFetchEventReservationsTicketsQuery,
  useFetchEventAttendeesQuery,
  useFetchEventReferralsQuery,
  useFetchStoresQuery,
  useFetchRoomsQuery,
  useFetchProductsQuery,
  useFetchProductCategoriesQuery,
  useFetchOrdersQuery,
  useFetchNotificationsQuery,
  useFetchReferralRequestQuery,
  useFetchPromotersGroupsQuery,
  useAddTicketMutation,
  useAddBasicticketMutation,
  useEditTicketMutation,
  useRemoveTicketMutation,
  useRemovePromoterMutation,
  useAddEventMutation,
  useEditEventMutation,
  useUpdateEventMutation,
  usePublishEventMutation,
  useCreationStepEventMutation,
  useCreateEventLinkMutation,
  useCancelEventMutation,
  useArchiveEventMutation,
  useEditEventCoverMutation,
  useEditEventInvoiceInfosMutation,
  useRemoveEventMutation,
  useAddOrgMutation,
  useAddFullOrgMutation,
  useEditOrgMutation,
  useEditOrgCoverMutation,
  useEditOrgLogoMutation,
  useRemoveOrgMutation,
  useEditUserMutation,
  useEditUserStatusMutation,
  useEditUserAccountIdMutation,
  useEditOrgAccountIdMutation,
  useEditUserProfilePictureMutation,
  useEditEvntOrgMutation,
  useAddReferralsMutation,
  useAddStoreMutation,
  useEditStoreMutation,
  useEditStoreCoverPictureMutation,
  useAddRoomMutation,
  useRemoveRoomMutation,
  useAddTableMutation,
  useRemoveTableMutation,
  useAddProductMutation,
  useEditProductMutation,
  useRemoveProductMutation,
  useAddCategoryMutation,
  useAddOrderMutation,
  useEditOrderMutation,
  useRemoveOrderMutation,
  useAddNotificationMutation,
  useEditNotificationMutation,
  useAddRpRequestMutation,
  useCancelRpRequestMutation,
  useFetchPromotionRequestsQuery,
  useAcceptRequestMutation,
  useRejectRequestMutation,
  useRemovePromotersGroupMutation,
  useAddRpInvitationMutation,
  useFetchGroupRequestsQuery,
  useFetchGroupRequestsByGroupIdQuery,
  useAddEventMenuMutation,
  useFetchEventMenuQuery,
  useAddEventProductCategoryMutation,
  useFetchEventProdCategoriesQuery,
  useEditEventProductMutation,
  useRemoveEventProductMutation,
  useRemoveStoreMutation,
  useFetchUserPublishedEventsQuery,
  useEditEventOrderMutation,
  useRemoveEventOrderMutation,
  useFetchReservationsQuery,
  useFetchEventDjsQuery,
  useAddDjMutation,
  useRemoveDjMutation,
  useSaveSeatmapMutation,
  useLoadSeatmapQuery,
  useFetchTableProductsMutation,
  useRemoveTableProductMutation,
  useGetMailCampaignsQuery,
  useGetMailCampaignQuery,
  useCreateMailCampaignMutation,
  useUpdateMailCampaignMutation,
  useEditMailCampaignMutation,
  useDeleteMailCampaignMutation,
  useGetEventStatsRealtimeQuery,
  useUpdateEventSalesStatusMutation,
  useUpdateEventAccessRestrictionMutation,
} = EventsApi;
